import { AxiosError } from 'axios'

/**
 * Client side of the licence model (docs/plans/model-licencji.md, "Klient: co widzi
 * użytkownik"). The server decides every write; these helpers only explain a refusal and
 * warn ahead of one.
 */

/** `LicenceService::status()`: computed per read, `active` while enforcement is off. */
export type LicenceStatusT = 'active' | 'expiring' | 'expired'

/** `licence` on /users/me (UserLicenceNormalizer): the ACTIVE organization's licence. */
export type UserLicenceT = {
  status: LicenceStatusT
  expiresAt: string | null
  /** Only for an admin/owner of that organization and for special users. */
  userLimit?: number | null
}

/** `demo` on the project embedded in a stage read (ProjectLicenceNormalizer). */
export type ProjectDemoT = {
  expiresAt: string | null
  userLimit: number | null
  issueLimit: number | null
} | null

/** The licence fields ProjectLicenceNormalizer adds to the project of a stage read. */
export type StageProjectLicenceT = {
  demo?: ProjectDemoT
  /** The licence of the organization that OWNS the project: it decides about writes (W1). */
  ownerLicenceStatus?: LicenceStatusT | null
  organization?: { id?: number } | null
  /**
   * Whether the server's write guard applies to this reader at all
   * (LicenceWriteGuard::appliesToCurrentRequest): false while enforcement is off and for a
   * special user. Absent (a read cached by an older build, or an older server) counts as
   * false: the client only warns, and a false warning disables a stage the server accepts.
   */
  licenceChecksApply?: boolean
}

/** `X-Licence-Block` values (LicenceBlockedException::REASON_*). */
export const LICENCE_BLOCK_REASONS = [
  'licence_expired',
  'demo_expired',
  'no_licence',
  'user_limit',
  'demo_user_limit',
  'demo_issue_limit',
  // Not a licence matter (D7): an archived project refuses writes until it is restored.
  'project_archived',
] as const
export type LicenceBlockReasonT = (typeof LICENCE_BLOCK_REASONS)[number]

export const LICENCE_BLOCK_HEADER = 'X-Licence-Block'

export type LicenceContactT = { email?: string | null; phone?: string | null }

/**
 * The reason of a licence refusal, or null for any other error. Recognised by the header,
 * not the status: a licence refusal is a 403 like any permission refusal, and the header is
 * what tells them apart (as `X-Maintenance-Mode` does for the 503). A value this build does
 * not know returns null, so the refusal falls back to the generic 403 handling instead of
 * being explained with the wrong words.
 */
export function getLicenceBlockReason(error: unknown): LicenceBlockReasonT | null {
  if (!(error instanceof AxiosError) || error.response?.status !== 403) return null
  const headers = error.response.headers as unknown as {
    get?: (name: string) => unknown
    [key: string]: unknown
  }
  const raw =
    typeof headers?.get === 'function'
      ? headers.get(LICENCE_BLOCK_HEADER)
      : headers?.[LICENCE_BLOCK_HEADER.toLowerCase()]
  const reason = String(raw ?? '').trim()
  return (LICENCE_BLOCK_REASONS as readonly string[]).includes(reason)
    ? (reason as LicenceBlockReasonT)
    : null
}

/** The contact the refusal carries (problem+json `contact`), so the toast needs no request. */
export function getLicenceBlockContact(error: unknown): LicenceContactT | null {
  if (!(error instanceof AxiosError)) return null
  const contact = (error.response?.data as { contact?: LicenceContactT } | undefined)?.contact
  return contact && (contact.email || contact.phone) ? contact : null
}

/** `now >= expiresAt`, the same boundary as the server. Null = not a demo, or no term. */
export function isDemoExpired(demo: ProjectDemoT | undefined, now: Date): boolean {
  if (!demo?.expiresAt) return false
  return now.getTime() >= new Date(demo.expiresAt).getTime()
}

/**
 * Whether new content can be added on a stage, judged from the OWNER's licence and the
 * demo clock (a demo needs both valid). Front-end only warns: the server decides.
 */
export function resolveStageWriteBlock(
  project: StageProjectLicenceT | null | undefined,
  now: Date,
): Extract<LicenceBlockReasonT, 'licence_expired' | 'demo_expired'> | null {
  if (!project || project.licenceChecksApply !== true) return null
  if (project.ownerLicenceStatus === 'expired') return 'licence_expired'
  if (isDemoExpired(project.demo, now)) return 'demo_expired'
  return null
}
